import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface CuentaBancariaVes {
  cuentaBancariaVesId: number;
  nombreTitular: string;
  rifCedula: string;
  numeroCuenta: string;
  tipoCuenta: string;
  banco: string;
  activo: number;
  createdAt: string;
  updatedAt: string;
}

export interface BancoVe {
  nombre: string;
  codigoBanco: string;
}

export interface CreateCuentaBancariaVesDto {
  nombreTitular: string;
  rifCedula: string;
  numeroCuenta: string;
  tipoCuenta: string;
  banco: string;
}

@Injectable({
  providedIn: 'root',
})
export class CuentasBancariasVesService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  getAll(): Observable<CuentaBancariaVes[]> {
    const url = `${this.baseUrl}cuentas-bancarias-ves`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<CuentaBancariaVes[]>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getBancosVe(): Observable<BancoVe[]> {
    const url = `${this.baseUrl}cuentas-bancarias-ves/bancos-ve`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<BancoVe[]>(url, { headers })),
      catchError(this.handleError),
    );
  }

  create(dto: CreateCuentaBancariaVesDto): Observable<CuentaBancariaVes> {
    const url = `${this.baseUrl}cuentas-bancarias-ves`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<CuentaBancariaVes>(url, dto, { headers })),
      catchError(this.handleError),
    );
  }

  getActiveClient(): Observable<CuentaBancariaVes[]> {
    const url = `${this.baseUrl}cuentas-bancarias-ves-client`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<CuentaBancariaVes[]>(url, { headers })),
      catchError(this.handleError),
    );
  }

  toggleActive(id: number): Observable<CuentaBancariaVes> {
    const url = `${this.baseUrl}cuentas-bancarias-ves/${id}/toggle-active`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<CuentaBancariaVes>(url, {}, { headers })),
      catchError(this.handleError),
    );
  }
}
